import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';
import ImageCropModal from '../../components/ImageCropModal';

interface SliderImage {
  id: number;
  imageS3: string | null;
}

export default function AdminSliderPage() {
  const [slides, setSlides] = useState<SliderImage[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const load = () =>
    axios.get(`${API_BASE}/slider`).then(r => setSlides(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f) setPendingFile(f);
    e.target.value = '';
  };

  const handleCropped = (cropped: File) => {
    setFile(cropped);
    setPreview(URL.createObjectURL(cropped));
    setPendingFile(null);
  };

  const upload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      await api.post('/slider', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null);
      setPreview(null);
      await load();
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Удалить изображение из слайдера?')) return;
    await api.delete(`/slider/${id}`);
    await load();
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Управление слайдером</h2>

      {/* Загрузка нового изображения */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>➕ Добавить изображение</h3>
        <form onSubmit={upload} style={styles.form}>
          <label style={styles.fileLabel}>
            📎 Выбрать изображение
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </label>
          {file && <span style={styles.fileName}>{file.name}</span>}

          {preview && (
            <img src={preview} alt="preview" style={styles.preview} />
          )}

          <button style={styles.btn} type="submit" disabled={!file || uploading}>
            {uploading ? 'Загрузка...' : 'Загрузить'}
          </button>
        </form>
      </div>

      {/* Текущие изображения */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Изображения в слайдере ({slides.length})</h3>
        {slides.length === 0 ? (
          <p style={styles.empty}>Изображений пока нет</p>
        ) : (
          <div style={styles.grid}>
            {slides.map((slide, i) => (
              <div key={slide.id} style={styles.slideCard}>
                {slide.imageS3 ? (
                  <img
                    src={`${API_BASE}/slider/images/${slide.imageS3}`}
                    alt={`Слайд ${i + 1}`}
                    style={styles.slideImg}
                  />
                ) : (
                  <div style={styles.slidePlaceholder}>Нет изображения</div>
                )}
                <div style={styles.slideFooter}>
                  <span style={styles.slideNum}>Слайд {i + 1}</span>
                  <button onClick={() => remove(slide.id)} style={styles.btnDanger}>
                    🗑 Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageCropModal
        file={pendingFile}
        aspect={16 / 9}
        title="Обрезать изображение слайдера"
        onCancel={() => setPendingFile(null)}
        onCropped={handleCropped}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 900, margin: '0 auto' },
  title: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  card: {
    background: '#fff', borderRadius: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    marginBottom: 20, overflow: 'hidden',
  },
  cardTitle: {
    color: '#1a3a6b', fontSize: 'clamp(0.9rem, 2vw, 1rem)',
    padding: '16px 20px', borderBottom: '1px solid #f0f4fb',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  fileLabel: {
    display: 'inline-block', padding: '10px 20px',
    background: '#f0f4fb', border: '1px dashed #1a3a6b',
    borderRadius: 8, cursor: 'pointer',
    fontSize: '0.9rem', color: '#1a3a6b', fontWeight: 600,
    alignSelf: 'flex-start',
  },
  fileName: { fontSize: '0.85rem', color: '#555', wordBreak: 'break-word' },
  preview: {
    width: '100%', maxHeight: 240,
    objectFit: 'cover', borderRadius: 8,
    border: '1px solid #dde3ef',
  },
  btn: {
    padding: '10px 24px', background: '#1a3a6b',
    color: '#fff', border: 'none', borderRadius: 8,
    fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start',
  },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))',
    gap: 16, padding: '16px 20px 20px',
  },
  slideCard: {
    border: '1px solid #e0e6f0', borderRadius: 8, overflow: 'hidden',
  },
  slideImg: { width: '100%', height: 160, objectFit: 'cover', display: 'block' },
  slidePlaceholder: {
    width: '100%', height: 160,
    background: 'linear-gradient(135deg, #1a3a6b, #2d6abf)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem',
  },
  slideFooter: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '10px 12px',
    background: '#f8f9fa', flexWrap: 'wrap', gap: 8,
  },
  slideNum: { fontSize: '0.85rem', color: '#555', fontWeight: 600 },
  btnDanger: {
    padding: '5px 10px', background: '#e74c3c',
    color: '#fff', border: 'none', borderRadius: 6,
    fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap',
  },
};
